import React from "react";
import { Box } from "@mui/material";
import colors from "../../../../utils/colors";
import { SslType } from "../../../../utils/constants";

/**
 * Imperva-style SSL flow: User → Layerd → Origin.
 * Palette mirrors SSLTLSPage certs theme (muted primary/green — not neon).
 */

// Same tokens as SSLTLSPage encryption ladder / info banners
const INDIGO = colors.primary[300];
const INDIGO_INK = colors.primary[500];
const INDIGO_SOFT = "rgba(99,113,247,0.08)";
const INDIGO_BORDER = "rgba(99,113,247,0.22)";
/** Secure hop: use dark green (green[300]) — green[200] is too bright on stage */
const ENC = colors.green[300];
const ENC_FILL = "rgba(23, 143, 79, 0.12)";
const ENC_INK = colors.green[300];
const WARN = colors.yellow[200];
const DANGER = colors.red[200];
const INK = colors.neutral[1000];
const MUTED = colors.neutral[700];
const FAINT = colors.neutral[600];
const LINE = colors.neutral[200];
const SURFACE = "#FFFFFF";
/** Soft stage — matches CERTS_HEADING_BG family, not a loud indigo wash */
const STAGE = "rgb(245, 246, 254)";
const BRAND = colors.primary[400];
const BRAND_SOFT = colors.primary[100];

/** Shared layout — one horizontal axis for arrows + node centers */
const FLOW_Y = 112;
const SVG_W = 920;
const USER_X = 96;
const CLOUD_X = 460;
const ORIGIN_X = 824;
const USER_R = 34;
const CLOUD_R = 40;
const ORIGIN_R = 38;
/** Gap between circle edge and arrow tip */
const ARROW_GAP = 10;
const CALLOUT_W = 280;

function hopStyle(kind) {
  switch (kind) {
    case "validated":
      return { stroke: ENC, label: "Encrypted traffic", sub: "Validated", dashed: false };
    case "unverified":
      return { stroke: ENC, label: "Encrypted traffic", sub: "Not validated", dashed: false };
    case "secure":
      return { stroke: ENC, label: "Encrypted traffic", sub: null, dashed: false };
    case "plain":
    default:
      return { stroke: colors.neutral[400], label: "Plaintext traffic", sub: null, dashed: true };
  }
}

function hopsForMode(mode) {
  switch (mode) {
    case SslType.OFF:
      return { left: "plain", right: "plain" };
    case SslType.FLEXIBLE:
      return { left: "secure", right: "plain" };
    case SslType.FULL:
      return { left: "secure", right: "unverified" };
    case SslType.FULL_STRICT:
    default:
      return { left: "secure", right: "validated" };
  }
}

function calloutsForMode(mode) {
  switch (mode) {
    case SslType.OFF:
      return {
        left: "No Layerd SSL termination — traffic is unencrypted.",
        right: "Origin is reached without TLS from Layerd.",
        leftTone: "warn",
        rightTone: "warn",
      };
    case SslType.FLEXIBLE:
      return {
        left: "A Layerd SSL certificate is provided free of charge.",
        right: "Origin may stay on HTTP — no cert required yet.",
        leftTone: "brand",
        rightTone: "neutral",
      };
    case SslType.FULL:
      return {
        left: "Visitor traffic is encrypted to Layerd.",
        right: "Your certificate remains on the origin (self-signed allowed).",
        leftTone: "brand",
        rightTone: "ok",
      };
    case SslType.FULL_STRICT:
    default:
      return {
        left: "A Layerd SSL certificate is provided free of charge.",
        right: "Your original certificate remains on your server and must be trusted.",
        leftTone: "brand",
        rightTone: "ok",
      };
  }
}

/** x1/x2 = arrowhead tips on FLOW_Y between nodes. */
function DoubleArrow({ x1, x2, y, stroke, dashed }) {
  const head = 11;
  const dash = dashed ? "5 5" : undefined;
  return (
    <g>
      <line
        x1={x1 + head}
        y1={y}
        x2={x2 - head}
        y2={y}
        stroke={stroke}
        strokeWidth={2.4}
        strokeDasharray={dash}
        strokeLinecap="round"
        opacity={dashed ? 0.75 : 0.92}
      />
      <path
        d={`M ${x1 + head + 1} ${y - 6.5} L ${x1} ${y} L ${x1 + head + 1} ${y + 6.5}`}
        fill="none"
        stroke={stroke}
        strokeWidth={2.4}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={dashed ? 0.75 : 0.92}
      />
      <path
        d={`M ${x2 - head - 1} ${y - 6.5} L ${x2} ${y} L ${x2 - head - 1} ${y + 6.5}`}
        fill="none"
        stroke={stroke}
        strokeWidth={2.4}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={dashed ? 0.75 : 0.92}
      />
    </g>
  );
}

function HopLabel({ x, y, primary, sub }) {
  const w = Math.max(118, (primary?.length || 0) * 7.2 + (sub ? 40 : 16));
  const h = sub ? 34 : 24;
  return (
    <g transform={`translate(${x - w / 2}, ${y - h - 8})`}>
      <rect
        width={w}
        height={h}
        rx={h / 2}
        fill={SURFACE}
        stroke={LINE}
        strokeWidth={1}
        filter="url(#ldSoftShadow)"
      />
      <text
        x={w / 2}
        y={sub ? 14 : 16}
        textAnchor="middle"
        fill={colors.neutral[800]}
        fontSize="11.5"
        fontWeight="600"
        fontFamily="inherit"
      >
        {primary}
      </text>
      {sub && (
        <text
          x={w / 2}
          y={28}
          textAnchor="middle"
          fill={FAINT}
          fontSize="10"
          fontWeight="500"
          fontFamily="inherit"
        >
          {sub}
        </text>
      )}
    </g>
  );
}

function NodePlate({ cx, cy, r, active }) {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={SURFACE}
      stroke={active ? INDIGO_BORDER : LINE}
      strokeWidth={active ? 1.75 : 1.25}
      filter="url(#ldSoftShadow)"
    />
  );
}

function UserIcon({ cx, cy, r }) {
  return (
    <g>
      <NodePlate cx={cx} cy={cy} r={r} />
      <g
        transform={`translate(${cx}, ${cy + 2})`}
        fill="none"
        stroke={INDIGO_INK}
        strokeWidth="1.85"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="0" cy="-8" r="6.5" />
        <path d="M-12 14c1.5-9 5.5-13 12-13s10.5 4 12 13" />
      </g>
    </g>
  );
}

function CloudIcon({ cx, cy, r, secure }) {
  return (
    <g>
      <NodePlate cx={cx} cy={cy} r={r} active />
      <circle cx={cx} cy={cy} r={r - 4} fill={BRAND_SOFT} opacity={0.75} />
      <g transform={`translate(${cx}, ${cy + 1})`}>
        <path
          d="M-14 12H16c7 0 12-4.8 12-11S22.5-11 16-11.5C15-18 9.5-21 3-21c-5.5 0-10.5 2.8-12.5 7C-10-14.5-11-14.5-12-14.5-18.5-14.5-24-9-24-2.5c0 7.5 6.5 14.5 10 14.5z"
          fill={SURFACE}
          stroke={INDIGO_INK}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <g transform="translate(-8, -10)">
          <path
            d="M8 0 L15 3 V9c0 4.6-3 8.2-7 9.6C4 17.2 1 13.6 1 9V3L8 0z"
            fill={secure ? ENC_FILL : colors.neutral[100]}
            stroke={secure ? ENC : MUTED}
            strokeWidth="1.25"
          />
          {secure ? (
            <path
              d="M4.8 8.4l2.1 2.1 4.3-4.4"
              fill="none"
              stroke={ENC_INK}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <text x="8" y="11" textAnchor="middle" fill={MUTED} fontSize="8" fontWeight="700" fontFamily="inherit">
              !
            </text>
          )}
        </g>
      </g>
    </g>
  );
}

function ServersIcon({ cx, cy, r, validated, secureHop }) {
  return (
    <g>
      <NodePlate cx={cx} cy={cy} r={r} />
      <g transform={`translate(${cx}, ${cy})`}>
        {[-13, 0, 13].map((dy, i) => (
          <g key={i} transform={`translate(0, ${dy})`}>
            <rect
              x={-18}
              y={-5.5}
              width={36}
              height={11}
              rx={2.5}
              fill={colors.neutral[50]}
              stroke={MUTED}
              strokeWidth="1.3"
            />
            <circle cx={-11} cy={0} r={1.6} fill={secureHop ? ENC : colors.neutral[400]} />
            <line x1={-5} y1={0} x2={12} y2={0} stroke={LINE} strokeWidth="1.3" strokeLinecap="round" />
          </g>
        ))}
        <g transform="translate(22, 0)">
          <circle
            cx={0}
            cy={0}
            r={10}
            fill={SURFACE}
            stroke={validated ? ENC : secureHop ? WARN : LINE}
            strokeWidth="1.4"
          />
          {validated ? (
            <path
              d="M-4 0.2l2.6 2.6 5.6-5.8"
              fill="none"
              stroke={ENC_INK}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <text x="0" y="4" textAnchor="middle" fill={MUTED} fontSize="11" fontWeight="700" fontFamily="inherit">
              ?
            </text>
          )}
        </g>
      </g>
    </g>
  );
}

/**
 * Light-theme callout cards (all tones share the same layout):
 * soft fill + tinted border + left icon + dark body text.
 * Matches product alert/banner pattern on SSL TLS.
 */
function toneStyle(tone) {
  switch (tone) {
    case "warn":
      return {
        bg: "rgba(208, 4, 22, 0.05)",
        border: "rgba(208, 4, 22, 0.28)",
        accent: colors.red[200],
        icon: "warn",
      };
    case "brand":
      return {
        bg: colors.primary[100],
        border: "rgba(99, 113, 247, 0.28)",
        accent: colors.primary[400],
        icon: "lock",
      };
    case "ok":
      return {
        bg: "rgba(23, 143, 79, 0.06)",
        border: "rgba(23, 143, 79, 0.28)",
        accent: colors.green[300],
        icon: "check",
      };
    default:
      return {
        bg: SURFACE,
        border: colors.neutral[200],
        accent: colors.neutral[600],
        icon: "info",
      };
  }
}

function CalloutIcon({ kind, accent, x, y }) {
  // 18×18 glyph block, vertically centered with first text line
  const g = `translate(${x}, ${y})`;
  if (kind === "warn") {
    return (
      <g transform={g} fill="none" stroke={accent} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round">
        <path d="M9 2.5 L16.5 15.5 H1.5 Z" />
        <line x1="9" y1="7" x2="9" y2="11" />
        <circle cx="9" cy="13.5" r="0.75" fill={accent} stroke="none" />
      </g>
    );
  }
  if (kind === "lock") {
    return (
      <g transform={g} fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5.5 8V6.2a3.5 3.5 0 017 0V8" />
        <rect x="4" y="8" width="10" height="8" rx="1.5" />
        <circle cx="9" cy="12" r="1" fill={accent} stroke="none" />
      </g>
    );
  }
  if (kind === "check") {
    return (
      <g transform={g} fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="9" r="7" />
        <path d="M5.5 9.2l2.4 2.4 4.6-4.8" />
      </g>
    );
  }
  // info
  return (
    <g transform={g} fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round">
      <circle cx="9" cy="9" r="7" />
      <line x1="9" y1="8" x2="9" y2="12.5" />
      <circle cx="9" cy="5.5" r="0.75" fill={accent} stroke="none" />
    </g>
  );
}

function Callout({ y, width, text, tone, pointToX, pointToY }) {
  const t = toneStyle(tone);
  const lines = wrapText(text, 28);
  const padY = 12;
  const padX = 14;
  const iconSize = 18;
  const gap = 10;
  const lineH = 15;
  const textBlockH = Math.max(iconSize, lines.length * lineH);
  const h = padY * 2 + textBlockH;
  const x = Math.max(12, Math.min(pointToX - width / 2, SVG_W - width - 12));
  const midX = pointToX;
  const tipY = pointToY;
  const headH = 7;
  const shaftTop = tipY + headH;
  const textX = x + padX + iconSize + gap;
  const iconY = y + (h - iconSize) / 2;
  const textStartY = y + padY + 11;

  return (
    <g>
      <line
        x1={midX}
        y1={y}
        x2={midX}
        y2={shaftTop}
        stroke={t.border}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <polygon
        points={`${midX},${tipY} ${midX - 5.5},${tipY + headH} ${midX + 5.5},${tipY + headH}`}
        fill={t.border}
      />
      <rect
        x={x}
        y={y}
        width={width}
        height={h}
        rx={8}
        fill={t.bg}
        stroke={t.border}
        strokeWidth={1.25}
        filter="url(#ldSoftShadow)"
      />
      <CalloutIcon kind={t.icon} accent={t.accent} x={x + padX} y={iconY} />
      {lines.map((line, i) => (
        <text
          key={i}
          x={textX}
          y={textStartY + i * lineH}
          fill={INK}
          fontSize="12"
          fontWeight="400"
          fontFamily="inherit"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

function wrapText(text, maxChars) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length > maxChars && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = next;
    }
  });
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}

function NodeCaption({ x, y, label, emphasis }) {
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={emphasis ? INDIGO_INK : INK}
      fontSize={emphasis ? 13 : 12.5}
      fontWeight={emphasis ? 700 : 600}
      fontFamily="inherit"
    >
      {label}
    </text>
  );
}

export default function SslModeFlowDiagram({ mode = SslType.FULL_STRICT }) {
  const hops = hopsForMode(mode);
  const left = hopStyle(hops.left);
  const right = hopStyle(hops.right);
  const callouts = calloutsForMode(mode);
  const secureCloud = hops.left !== "plain" || hops.right !== "plain";
  const originValidated = hops.right === "validated";
  const originSecureHop = hops.right !== "plain";

  // Arrow endpoints sit on plate edges so left hop, cloud center, right hop share FLOW_Y
  const leftX1 = USER_X + USER_R + ARROW_GAP;
  const leftX2 = CLOUD_X - CLOUD_R - ARROW_GAP;
  const rightX1 = CLOUD_X + CLOUD_R + ARROW_GAP;
  const rightX2 = ORIGIN_X - ORIGIN_R - ARROW_GAP;

  const leftMidX = (leftX1 + leftX2) / 2;
  const rightMidX = (rightX1 + rightX2) / 2;

  // Caption baseline under largest plate
  const captionY = FLOW_Y + CLOUD_R + 22;
  // Room under captions for the vertical leader + callout card
  const calloutY = 236;

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "12px",
        border: `1px solid ${LINE}`,
        background: `linear-gradient(180deg, ${STAGE} 0%, #FFFFFF 72%)`,
        boxShadow: "0 1px 2px rgba(17, 17, 17, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${SVG_W} 348`}
        role="img"
        aria-label="How Layerd handles SSL between visitors, Layerd, and origin servers"
        sx={{
          display: "block",
          width: "100%",
          height: "auto",
          fontFamily: "inherit",
        }}
      >
        <defs>
          <filter id="ldSoftShadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#111111" floodOpacity="0.07" />
          </filter>
          <linearGradient id="ldStageGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={STAGE} />
            <stop offset="100%" stopColor="#FFFFFF" />
          </linearGradient>
        </defs>

        <rect x="0" y="0" width={SVG_W} height="348" fill="url(#ldStageGrad)" />

        <line
          x1={leftX1}
          y1={FLOW_Y}
          x2={rightX2}
          y2={FLOW_Y}
          stroke={INDIGO_BORDER}
          strokeWidth="1"
          strokeDasharray="2 6"
          opacity="0.3"
        />

        <DoubleArrow x1={leftX1} x2={leftX2} y={FLOW_Y} stroke={left.stroke} dashed={left.dashed} />
        <HopLabel x={leftMidX} y={FLOW_Y - 10} primary={left.label} sub={left.sub} />

        <DoubleArrow x1={rightX1} x2={rightX2} y={FLOW_Y} stroke={right.stroke} dashed={right.dashed} />
        <HopLabel x={rightMidX} y={FLOW_Y - 10} primary={right.label} sub={right.sub} />

        <UserIcon cx={USER_X} cy={FLOW_Y} r={USER_R} />
        <CloudIcon cx={CLOUD_X} cy={FLOW_Y} r={CLOUD_R} secure={secureCloud} />
        <ServersIcon
          cx={ORIGIN_X}
          cy={FLOW_Y}
          r={ORIGIN_R}
          validated={originValidated}
          secureHop={originSecureHop}
        />

        <NodeCaption x={USER_X} y={captionY} label="User" />
        <NodeCaption x={CLOUD_X} y={captionY} label="Layerd" emphasis />
        <NodeCaption x={ORIGIN_X} y={captionY} label="Origin servers" />

        {/* Vertical leaders only — tip meets green/grey hop line exactly */}
        <Callout
          y={calloutY}
          width={CALLOUT_W}
          text={callouts.left}
          tone={callouts.leftTone}
          pointToX={leftMidX}
          pointToY={FLOW_Y}
        />
        <Callout
          y={calloutY}
          width={CALLOUT_W}
          text={callouts.right}
          tone={callouts.rightTone}
          pointToX={rightMidX}
          pointToY={FLOW_Y}
        />
      </Box>
    </Box>
  );
}
